'use client';

import React, { useState, useEffect } from "react";
import {
  List,
  ListItem,
  IconButton,
  Typography,
  Box,
  Button,
  CircularProgress,
} from "@mui/material";
import {
  DragIndicator,
  Delete,
  LocalShipping,
  Warehouse,
} from "@mui/icons-material";
import { useStyles } from "./DeliveryFlowList.style";
import DeliveryFlowListProperties from "./DeliveryFlowListProperties";
import DeliveryStepResponse from "../../../../model/articleDelivery/DeliveryStepResponse";
import WarehouseStepResponse from "../../../../model/articleDelivery/WarehouseStepResponse";
import WarehouseStepStatus from "../../../../entities/WarehouseStepStatus";
import { Observer } from "mobx-react-lite";
import ShowDeliveryStepStatus from "../../ShowDeliveryStepStatus/ShowDeliveryStepStatus";
import Currency from "../../../../entities/Currency";
import PriceInput from "../../../Inputs/PriceInput/PricetInput";
import CurrencySelect from "../../../Selects/CurrencySelect/CurrencySelect";
import Checkbox from "../../../../ui_components/Checkbox/Checkbox";
import ConfirmArticleOrderReceivedButton from "../../../Buttons/ConfirmArticleOrderReceivedButton/ConfirmArticleOrderReceivedButton";
import DeliveryStepStatus from "../../../../entities/DeliveryStepStatus";
import ProductOrderStatus from "../../../../entities/productOrder/ProductOrderStatus";
import ConfirmArticleOrderDeliveredButton from "../../../Buttons/ConfirmArticleOrderDeliveredButton/ConfirmArticleOrderDeliveredButton";
import useConfirmation from "../../../../hooks/ConfirmationDialog";
import AllowAccess from "../../../Access/AllowAccess/AllowAccess";
import Access from "../../../Access/AllowAccess/Access";
import { ArticleDeliveryService } from "../../../../service/articleDelivery/ArticleDeliveryService";
import { ArticleWarehouseService } from "../../../../service/ArticleWarehouseService";
import ArticleWarehouse from "../../../../entities/productOrder/article/ArticleWarehouse";
import Modal from "../../../../ui_components/Modal/Modal";
import Link from "../../../../ui_components/Link/Link";

const DeliveryFlowList: React.FC<DeliveryFlowListProperties> = ({
  articleDeliveryId,
  articleDeliveryStatus,
  haveDeliveryPersonSelected,
  deliveryFlow,
  warehouseStep = null,
  onUpdate,
  onWarehouseStepChange,
  onSave,
  onWarehouseDispatched,
  loading = false,
  readOnly = false,
}) => {
  const classes = useStyles();
  const confirm = useConfirmation();
  const [items, setItems] = useState<DeliveryStepResponse[]>(deliveryFlow);
  const [draggedItem, setDraggedItem] = useState<number | null>(null);

  const [warehouseModalOpen, setWarehouseModalOpen] = useState(false);
  const [warehouses, setWarehouses] = useState<ArticleWarehouse[]>([]);
  const [warehouseSearchFilter, setWarehouseSearchFilter] = useState("");
  const [warehouseLoading, setWarehouseLoading] = useState(false);
  const [dispatching, setDispatching] = useState(false);

  useEffect(() => {
    setItems(deliveryFlow);
  }, [deliveryFlow]);

  const fetchWarehouses = async (filter: string = "") => {
    setWarehouseLoading(true);
    try {
      const response = await ArticleWarehouseService.get(1, 100, filter || undefined);
      if (response.success && response.data) {
        setWarehouses(response.data.data || []);
      }
    } finally {
      setWarehouseLoading(false);
    }
  };

  const handleOpenWarehouseModal = async () => {
    setWarehouseModalOpen(true);
    setWarehouseSearchFilter("");
    await fetchWarehouses();
  };

  const handleSelectWarehouse = (warehouse: ArticleWarehouse) => {
    const newStep = new WarehouseStepResponse(
      "",
      warehouse.id,
      warehouse.name,
      WarehouseStepStatus.PENDING,
      Date.now(),
    );
    onWarehouseStepChange(newStep);
    setWarehouseModalOpen(false);
  };

  const handleRemoveWarehouseStep = () => {
    onWarehouseStepChange(null);
  };

  const handleDispatchWarehouseStep = async () => {
    if (!warehouseStep) return;
    const ok = await confirm(
      "Despachar desde almacén",
      "¿Está seguro de que desea despachar los artículos desde el almacén? Esta acción descontará el stock del almacén."
    );
    if (!ok) return;
    setDispatching(true);
    try {
      const response = await ArticleDeliveryService.dispatchWarehouseStep(
        articleDeliveryId,
        warehouseStep.warehouseId
      );
      if (response.success) {
        onWarehouseDispatched();
      }
    } finally {
      setDispatching(false);
    }
  };

  const isDispatched = warehouseStep?.status === WarehouseStepStatus.DISPATCHED;
  const anyStepStarted = items.some(
    (s) => s.status === DeliveryStepStatus.RECEIVED || s.status === DeliveryStepStatus.DELIVERED
  );
  const canManageWarehouseStep = !readOnly && !isDispatched && !anyStepStarted;

  const handleDragStart = (index: number) => {
    if (readOnly) return;
    setDraggedItem(index);
  };

  const handleDragOver = (e: React.DragEvent, index: number) => {
    e.preventDefault();
    if (readOnly || draggedItem === null || draggedItem === index) return;

    const newItems = [...items];
    const draggedItemContent = newItems[draggedItem];
    newItems.splice(draggedItem, 1);
    newItems.splice(index, 0, draggedItemContent);

    const updatedItems = newItems.map((item, idx) => ({
      ...item,
      order: idx + 1,
    }));

    setItems(updatedItems);
    onUpdate(updatedItems);
    setDraggedItem(index);
  };

  const handleDragEnd = () => {
    setDraggedItem(null);
  };

  const handleDelete = (index: number) => {
    if (readOnly) return;
    const newItems = items.filter((_, i) => i !== index);

    const updatedItems = newItems.map((item, idx) => ({
      ...item,
      order: idx + 1,
    }));

    setItems(updatedItems);
    onUpdate(updatedItems);
  };

  const handleAmountChange = (index: number, newValue: number) => {
    if (readOnly) return;
    const newItems = [...items];
    newItems[index] = {
      ...newItems[index],
      deliveryCost: newValue,
    };
    setItems(newItems);
    onUpdate(newItems);
  };

  const handleCurrencyChange = (index: number, newCurrency: Currency) => {
    if (readOnly) return;
    const newItems = [...items];
    newItems[index] = {
      ...newItems[index],
      deliveryCostCurrency: newCurrency,
    };
    setItems(newItems);
    onUpdate(newItems);
  };

  const handleSave = () => {
    onSave({ deliveryFlow: items, resetFlow: false, warehouseStep });
  };

  const handleSaveAndReset = async () => {
    const ok = await confirm(
      "¿Está seguro de que desea continuar?",
      "Esta acción reiniciará el flujo de entrega y asignará el primer repartidor como actual."
    );
    if (ok) {
        onSave({ deliveryFlow: items, resetFlow: true, warehouseStep });
    }
  };

  const isValidArticleDelivery = articleDeliveryStatus === ProductOrderStatus.ASSIGNED ||
    articleDeliveryStatus === ProductOrderStatus.PROCESSING;

  const hasFlowOrWarehouse = items.length > 0 || warehouseStep != null;

  return (
    <Observer>
      {() => (
        <Box>
          {/* Warehouse Step Section */}
          <AllowAccess any={[Access.SET_MONEY_DELIVERY_DELIVERY_PERSON]}>
            <Box sx={{ marginBottom: "16px" }}>
              {warehouseStep ? (
                <Box sx={{
                  border: "1px solid #f59e0b",
                  borderRadius: "8px",
                  padding: "12px 16px",
                  backgroundColor: "#fffbeb",
                }}>
                  <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: "8px" }}>
                      <Warehouse sx={{ color: "#f59e0b", fontSize: "20px" }} />
                      <Box>
                        <Typography sx={{ fontSize: "10px", fontWeight: 600, textTransform: "uppercase", color: "#92400e", letterSpacing: "0.05em" }}>
                          Paso de almacén
                        </Typography>
                        <Typography sx={{ fontSize: "14px", fontWeight: 500, color: "#1f2937" }}>
                          {warehouseStep.warehouseName ?? warehouseStep.warehouseId}
                        </Typography>
                      </Box>
                    </Box>
                    <Box sx={{ display: "flex", alignItems: "center", gap: "8px" }}>
                      {isDispatched ? (
                        <Typography sx={{
                          fontSize: "11px",
                          fontWeight: 600,
                          color: "#065f46",
                          backgroundColor: "#d1fae5",
                          padding: "2px 8px",
                          borderRadius: "4px",
                        }}>
                          Despachado
                        </Typography>
                      ) : (
                        <Typography sx={{
                          fontSize: "11px",
                          fontWeight: 600,
                          color: "#6b7280",
                          backgroundColor: "#f3f4f6",
                          padding: "2px 8px",
                          borderRadius: "4px",
                        }}>
                          Pendiente
                        </Typography>
                      )}
                      {canManageWarehouseStep && (
                        <IconButton
                          size="small"
                          onClick={handleRemoveWarehouseStep}
                          sx={{ color: "#ef4444", "&:hover": { backgroundColor: "rgba(239,68,68,0.08)" } }}
                        >
                          <Delete fontSize="small" />
                        </IconButton>
                      )}
                    </Box>
                  </Box>
                  {!readOnly && !isDispatched && warehouseStep.status === WarehouseStepStatus.PENDING && isValidArticleDelivery && (
                    <Box sx={{ marginTop: "8px", paddingTop: "8px", borderTop: "1px solid #fde68a" }}>
                      <Button
                        variant="text"
                        size="small"
                        onClick={handleDispatchWarehouseStep}
                        disabled={dispatching}
                        sx={{ color: "#92400e", fontSize: "12px", fontWeight: 600, textTransform: "none", "&:hover": { backgroundColor: "#fef3c7" } }}
                      >
                        {dispatching ? (
                          <>
                            <CircularProgress size={14} sx={{ marginRight: "4px" }} />
                            Despachando...
                          </>
                        ) : (
                          "Despachar"
                        )}
                      </Button>
                    </Box>
                  )}
                </Box>
              ) : (
                !readOnly && (
                  <Link
                    text="Añadir almacén como primer paso"
                    onClick={handleOpenWarehouseModal}
                  />
                )
              )}
            </Box>
          </AllowAccess>

          {/* Warehouse Selection Modal */}
          <Modal
            fullScreen={false}
            loading={warehouseLoading}
            title="Seleccionar almacén"
            onClose={() => setWarehouseModalOpen(false)}
            open={warehouseModalOpen}
            zIndex={1200}
          >
            <Box sx={{ padding: "16px", minWidth: "400px", maxHeight: "500px" }}>
              <input
                type="text"
                placeholder="Buscar almacén..."
                value={warehouseSearchFilter}
                onChange={(e) => {
                  setWarehouseSearchFilter(e.target.value);
                  fetchWarehouses(e.target.value);
                }}
                style={{
                  width: "100%",
                  padding: "8px 12px",
                  border: "1px solid #d1d5db",
                  borderRadius: "6px",
                  fontSize: "14px",
                  marginBottom: "12px",
                  outline: "none",
                }}
              />
              {warehouseLoading ? (
                <Box sx={{ display: "flex", justifyContent: "center", padding: "24px" }}>
                  <CircularProgress size={24} />
                </Box>
              ) : warehouses.length > 0 ? (
                <Box sx={{ maxHeight: "350px", overflowY: "auto" }}>
                  {warehouses.map((warehouse) => (
                    <Box
                      key={warehouse.id}
                      onClick={() => handleSelectWarehouse(warehouse)}
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: "12px",
                        padding: "10px 12px",
                        borderRadius: "6px",
                        cursor: "pointer",
                        border: "1px solid #e5e7eb",
                        marginBottom: "6px",
                        "&:hover": {
                          backgroundColor: "#eff6ff",
                          borderColor: "#93c5fd",
                        },
                      }}
                    >
                      <Warehouse sx={{ color: "#3b82f6", fontSize: "24px" }} />
                      <Typography sx={{ fontSize: "14px", fontWeight: 500 }}>
                        {warehouse.name}
                      </Typography>
                    </Box>
                  ))}
                </Box>
              ) : (
                <Typography sx={{ textAlign: "center", color: "#9ca3af", padding: "24px", fontSize: "14px" }}>
                  No se encontraron almacenes
                </Typography>
              )}
            </Box>
          </Modal>

          {/* Delivery Person Steps */}
          {items.length > 0 && <List className={classes.deliveryStepsList}>
            {items.map((step, index) => {
              const isLastItem = index === items.length - 1;
              const showCostInputs = !isLastItem;
              const showPayArticleConfectionPriceToDeliveryPerson = isLastItem;

              return (
                <ListItem
                  key={step.id || `delivery-${index}`}
                  draggable={!readOnly}
                  onDragStart={() => handleDragStart(index)}
                  onDragOver={(e) => handleDragOver(e, index)}
                  onDragEnd={handleDragEnd}
                  className={`${classes.deliveryStepItem} ${
                    draggedItem === index ? "dragging" : ""
                  } ${readOnly ? classes.readOnlyItem : ""}`}
                  sx={{ padding: 0 }}
                >
                  <Box className={classes.deliveryStepContent}>
                    <div className={classes.dragHandle}>
                      <DragIndicator />
                    </div>
                    <div className={classes.orderNumber}>{index + 1}</div>
                    <Box className={classes.deliveryStepInfo}>
                      <Typography className={classes.deliveryStepName}>
                        {step.deliveryPersonName}
                      </Typography>
                    </Box>
                  </Box>

                  {showCostInputs && (
                    <Box className={classes.costInputsContainer}>
                      <PriceInput
                        label="Comisión"
                        fullWidth
                        value={step.deliveryCost}
                        className={classes.costInput}
                        onChange={(newValue) => handleAmountChange(index, newValue)}
                        disabled={readOnly}
                      />
                      <CurrencySelect
                        selected={step.deliveryCostCurrency}
                        fullWidth
                        className={classes.costInput}
                        onChange={(newCurrency) => handleCurrencyChange(index, newCurrency)}
                        disabled={readOnly}
                      />
                    </Box>
                  )}

                  {showPayArticleConfectionPriceToDeliveryPerson && (
                    <Box>
                      <Checkbox
                        text={'Paga confección al repartidor'}
                        selected={step.payArticleConfectionPriceToDeliveryPerson}
                        onChange={(newValue) => {
                          if (readOnly) return;
                          const newItems = [...items];
                          newItems[index] = {
                            ...newItems[index],
                            payArticleConfectionPriceToDeliveryPerson: newValue,
                          };
                          setItems(newItems);
                          onUpdate(newItems);
                        }}
                        disabled={readOnly}
                      />
                    </Box>
                  )}

                  <Box className={classes.actionButtons}>
                    <div className={classes.status_container}>
                      <ShowDeliveryStepStatus status={step.status} />
                      {(step.status === DeliveryStepStatus.PENDING || step.status === DeliveryStepStatus.IN_PROGRESS) &&
                        isValidArticleDelivery && (
                        <ConfirmArticleOrderReceivedButton
                          deliveryPersonId={step.deliveryPersonId}
                          articleDeliveryId={articleDeliveryId}
                          onUpdate={(deliveryOrder) => {
                            const temp = deliveryOrder.deliveryFlow.map(t => {
                              return {...t, deliveryPersonName: items.find(x => x.id === t.id)?.deliveryPersonName ?? ''}}
                            )
                            setItems(temp);
                          }}
                        />
                      )}
                      {step.status === DeliveryStepStatus.RECEIVED &&
                        isValidArticleDelivery && (
                        <ConfirmArticleOrderDeliveredButton
                          deliveryPersonId={step.deliveryPersonId}
                          articleDeliveryId={articleDeliveryId}
                          onUpdate={(deliveryOrder) => {
                            const temp = deliveryOrder.deliveryFlow.map(t => {
                              return {...t, deliveryPersonName: items.find(x => x.id === t.id)?.deliveryPersonName ?? ''}}
                            )
                            setItems(temp);
                          }}
                        />
                      )}
                    </div>
                    <IconButton
                      size="small"
                      onClick={() => handleDelete(index)}
                      className={classes.deleteButton}
                      disabled={loading || readOnly}
                    >
                      <Delete fontSize="small" />
                    </IconButton>
                  </Box>
                </ListItem>
              );
            })}
          </List>}
          {items.length === 0 && !warehouseStep && (
            <Box className={classes.emptyState}>
              <LocalShipping className={classes.emptyStateIcon} />
              <Typography>No hay repartidores asignados</Typography>
            </Box>
          )}

          <div className={classes.buttons_container}>
            <Button
              variant="contained"
              color="primary"
              className={classes.saveButton}
              onClick={handleSave}
              disabled={loading || readOnly || !hasFlowOrWarehouse}
              fullWidth
            >
              {loading ? (
                <>
                  <CircularProgress size={20} sx={{ marginRight: "8px" }} />
                  Guardando...
                </>
              ) : (
                "Guardar"
              )}
            </Button>

            <Button
              variant="contained"
              color="primary"
              className={classes.saveButton}
              onClick={handleSaveAndReset}
              disabled={loading || readOnly}
              fullWidth
            >
              {loading ? (
                <>
                  <CircularProgress size={20} sx={{ marginRight: "8px" }} />
                  Guardando...
                </>
              ) : (
                "Guardar y Reiniciar"
              )}
            </Button>
          </div>

        </Box>
      )}
    </Observer>

  );
};

export default DeliveryFlowList;
